import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface PageHeaderState {
  title?: string;
  /** Small label rendered above the title to provide section/product context. */
  label?: string;
  breadcrumbs?: Breadcrumb[];
  /** Primary action rendered inline with the page title in the header bar. */
  actions?: ReactNode;
  /**
   * Slot izquierdo del header (back button, menu, logo). Lo consume el layout
   * para mostrarlo a la izquierda del título tanto en mobile como en desktop.
   */
  leading?: ReactNode;
  /**
   * Variante visual. `dashboard` (default) deja que el DashboardLayout pinte
   * su title-area. `flow` indica que la page renderiza su propio banner
   * (`<PageHeader variant="flow">`) y el del dashboard se oculta.
   */
  variant?: 'dashboard' | 'flow';
}

interface PageHeaderContextValue {
  header: PageHeaderState;
  setHeader: (header: PageHeaderState) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeader] = useState<PageHeaderState>({});
  const value = useMemo(() => ({ header, setHeader }), [header]);
  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

/**
 * Override the route-level page header at runtime. Use for dynamic titles,
 * e.g. detail pages where the title depends on a loaded entity
 * (`usePageHeader({ title: contact?.displayName })`).
 *
 * For static titles, prefer declaring them in the route's `handle`:
 *
 *   <Route handle={{ page: { titleKey: 'transfers.title' } }} ... />
 *
 * The override clears automatically on unmount.
 */
export function usePageHeader(header: PageHeaderState): void {
  const ctx = useContext(PageHeaderContext);
  // `setHeader` from useState is a stable reference — unlike `ctx` itself, which
  // is re-created by useMemo every time header state changes. Depending on `ctx`
  // would create a loop: setHeader → new header state → new ctx → effect re-runs.
  const setHeader = ctx?.setHeader;
  const crumbsKey = header.breadcrumbs
    ? header.breadcrumbs.map(c => `${c.label}|${c.href ?? ''}`).join(',')
    : '';

  useEffect(() => {
    if (!setHeader) return;
    setHeader({
      title: header.title,
      label: header.label,
      breadcrumbs: header.breadcrumbs,
      actions: header.actions,
      leading: header.leading,
      variant: header.variant,
    });
    return () => setHeader({});
    // ReactNode (actions, leading) intentionally omitted from deps — must be
    // stable (defined outside render or memoized by the caller).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeader, header.title, header.variant, crumbsKey]);
}

export function usePageHeaderOverride(): PageHeaderState {
  const ctx = useContext(PageHeaderContext);
  return ctx?.header ?? {};
}
